/* jshint esversion: 11 */

// Einrichtung der Ende-zu-Ende-Verschlüsselung und Verifikation eigener Devices.
// Gegenüber dem Nutzer heißt ein Device „App“: gemeint ist jede Anmeldung, ob
// Ziege auf dem Handy, im Browser oder Element.
//
// Der Zustand wird als ein Status-Objekt an Elm gemeldet; Elm schickt Aktionen
// zurück (siehe handleAction). Zustände (phase):
//   starting     – Krypto noch nicht bereit oder erster Sync steht aus
//   unavailable  – Rust-Crypto ließ sich nicht starten
//   setup        – das Konto hat noch keine Cross-Signing-Keys
//   unverified   – Keys existieren, dieses Device ist aber nicht verifiziert
//   ready        – dieses Device ist per Cross-Signing verifiziert
//
// Verifiziert wird per SAS (Emoji-Vergleich) mit einem anderen eigenen Device
// oder per Recovery-Key aus dem Secret Storage.
//
// Raumschlüssel gehen nur an Devices, die ihr Nutzer per Cross-Signing bestätigt
// hat (OnlySignedDevicesIsolationMode); Nachrichten unbestätigter Devices werden
// nicht entschlüsselt.

import {
    CryptoEvent, decodeRecoveryKey, OnlySignedDevicesIsolationMode, VerificationPhase,
    VerificationRequestEvent, VerifierEvent,
} from "matrix-js-sdk/lib/crypto-api";
import {decryptJson, encryptJson} from "./pickle-key.js";

const SAS_METHOD = "m.sas.v1";

// Ein angezeigter, aber noch nicht als notiert bestätigter Recovery-Key, verschlüsselt
// mit dem Pickle-Key. Wird die App vor der Bestätigung geschlossen, zeigt sie ihn beim
// nächsten Start erneut an.
const UNCONFIRMED_RECOVERY_KEY = "ziege_unconfirmed_recovery_key";

// Startet die Gegenseite eine angenommene Anfrage nicht selbst, beginnen wir den
// SAS-Austausch; die Spec regelt den Fall, dass beide gleichzeitig starten.
const SAS_START_FALLBACK_MS = 4000;

// Deutsche Bezeichnungen aus data-definitions/sas-emoji.json der Matrix-Spec
const SAS_EMOJI_NAMES_DE = {
    "dog": "Hund", "cat": "Katze", "lion": "Löwe", "horse": "Pferd", "unicorn": "Einhorn",
    "pig": "Schwein", "elephant": "Elefant", "rabbit": "Hase", "panda": "Panda", "rooster": "Hahn",
    "penguin": "Pinguin", "turtle": "Schildkröte", "fish": "Fisch", "octopus": "Oktopus",
    "butterfly": "Schmetterling", "flower": "Blume", "tree": "Baum", "cactus": "Kaktus",
    "mushroom": "Pilz", "globe": "Globus", "moon": "Mond", "cloud": "Wolke", "fire": "Feuer",
    "banana": "Banane", "apple": "Apfel", "strawberry": "Erdbeere", "corn": "Mais", "pizza": "Pizza",
    "cake": "Kuchen", "heart": "Herz", "smiley": "Lächeln", "robot": "Roboter", "hat": "Hut",
    "glasses": "Brille", "spanner": "Schraubenschlüssel", "santa": "Weihnachtsmann",
    "thumbs up": "Daumen Hoch", "umbrella": "Regenschirm", "hourglass": "Sanduhr", "clock": "Uhr",
    "gift": "Geschenk", "light bulb": "Glühbirne", "book": "Buch", "pencil": "Bleistift",
    "paperclip": "Büroklammer", "scissors": "Schere", "lock": "Schloss", "key": "Schlüssel",
    "hammer": "Hammer", "telephone": "Telefon", "flag": "Flagge", "train": "Zug",
    "bicycle": "Fahrrad", "aeroplane": "Flugzeug", "rocket": "Rakete", "trophy": "Pokal",
    "ball": "Ball", "guitar": "Gitarre", "trumpet": "Trompete", "bell": "Glocke", "anchor": "Anker",
    "headphones": "Kopfhörer", "folder": "Ordner", "pin": "Stecknadel",
};

const CANCELLATION_MESSAGES = {
    "m.user": "Die Verifikation wurde abgebrochen.",
    "m.timeout": "Die Verifikation hat zu lange gedauert.",
    "m.mismatched_sas": "Die Symbole stimmten nicht überein. Die Apps wurden nicht verifiziert.",
    "m.key_mismatch": "Die Schlüssel passen nicht zusammen. Die Apps wurden nicht verifiziert.",
    "m.accepted": "Die Anfrage wurde in einer anderen App angenommen.",
};

const STALE_REQUEST_MESSAGE = "Diese Anfrage ist inzwischen abgelaufen oder wurde in der anderen App abgebrochen. Bitte dort die Verifikation neu starten.";


export class CryptoSetup {

    // accountManagementUri: aus den OAuth-Metadaten des Homeservers (MAS-Kontoverwaltung)
    constructor(onStatus, {accountManagementUri = null} = {}) {
        this.onStatus = onStatus;
        this.accountManagementUri = accountManagementUri;
        this.client = null;
        this.secretStorageKeys = {};
        this.approvalWaiter = null;
        this.setupAttempted = false;
        this.request = null;
        this.sas = null;
        this.otherAppName = "";
        this.followedVerifiers = new WeakSet();
        this.status = {
            phase: "starting",
            busy: false,
            error: "",
            backupActive: false,
            recoveryKey: "",
            recoveryKeyReplaced: false,
            approvalUrl: "",
            verification: null,
        };

        // für sdk.createClient({cryptoCallbacks})
        this.cryptoCallbacks = {
            getSecretStorageKey: async ({keys}) => {
                for (const keyId of Object.keys(keys)) {
                    if (this.secretStorageKeys[keyId]) {
                        return [keyId, this.secretStorageKeys[keyId]];
                    }
                }
                return null;
            },
            cacheSecretStorageKey: (keyId, keyInfo, key) => {
                this.secretStorageKeys[keyId] = key;
            },
        };
    }

    publish(changes = {}) {
        Object.assign(this.status, changes);
        this.onStatus({...this.status});
    }

    get crypto() {
        return this.client?.getCrypto();
    }

    // Nach initRustCrypto(); ist Krypto nicht verfügbar, wird das nur gemeldet.
    attach(client, initError = null) {
        this.client = client;
        if (!this.crypto) {
            this.publish({
                phase: "unavailable",
                error: "Die Verschlüsselung ließ sich nicht starten" + (initError ? `: ${initError.message}` : "."),
            });
            return;
        }

        this.crypto.setDeviceIsolationMode(new OnlySignedDevicesIsolationMode());

        const refresh = () => this.refresh();
        this.crypto.on(CryptoEvent.UserTrustStatusChanged, refresh);
        this.crypto.on(CryptoEvent.KeysChanged, refresh);
        this.crypto.on(CryptoEvent.KeyBackupStatus, refresh);
        // Nach einer Verifikation lädt die andere App die Signatur für diese hoch;
        // hier kommt das nur als geänderte Geräteliste des eigenen Kontos an.
        this.crypto.on(CryptoEvent.DevicesUpdated, (userIds) => {
            if (userIds.includes(this.client.getUserId())) {
                refresh();
            }
        });
        this.crypto.on(CryptoEvent.KeyBackupDecryptionKeyCached, () => this.restoreBackupInBackground());
        this.crypto.on(CryptoEvent.VerificationRequestReceived, (request) => this.onIncomingRequest(request));
    }

    // Nach dem ersten Sync aufzurufen; erst ab dann wird der Zustand ermittelt.
    async start() {
        try {
            await this.waitForCurrentSecretStorageState();
        } catch (e) {
            console.error("Stand des Schlüsselspeichers nicht abgeglichen: ", e);
            this.publish({error: "Der Schlüsselspeicher ist gerade nicht erreichbar. Bitte die App später erneut öffnen."});
            return;
        }
        this.synced = true;
        await this.restoreUnconfirmedRecoveryKey();
        await this.refresh();
    }

    // Nach dem ersten Sync kann der lokale Stand der Account-Daten hinter dem Server
    // zurückliegen: Synapse beantwortet den initialen Sync einer Session eine Weile aus
    // einem Cache, spätere Änderungen folgen erst mit dem nächsten Sync. Auf veraltetem
    // Stand sähe das Secret Storage fehlend aus und würde neu angelegt, womit der
    // bestätigte Recovery-Key ungültig würde.
    async waitForCurrentSecretStorageState(timeoutMs = 60000) {
        const types = ["m.secret_storage.default_key", "m.cross_signing.master", "m.megolm_backup.v1"];
        const onServer = {};
        for (const type of types) {
            onServer[type] = JSON.stringify(await this.serverAccountData(type));
        }
        const inStore = (type) => JSON.stringify(this.client.getAccountData(type)?.getContent() ?? null);
        const deadline = Date.now() + timeoutMs;
        while (types.some((type) => inStore(type) !== onServer[type])) {
            if (Date.now() > deadline) {
                throw new Error("Account-Daten nicht synchronisiert");
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
    }

    async serverAccountData(type) {
        const path = `/user/${encodeURIComponent(this.client.getUserId())}/account_data/${type}`;
        try {
            return await this.client.http.authedRequest("GET", path);
        } catch (e) {
            if (e.httpStatus === 404) {
                return null;
            }
            throw e;
        }
    }

    // Ist der zwischengespeicherte Key schon der gültige Recovery-Key des Kontos, wird er
    // wieder angezeigt; ist er es noch nicht, bleibt er für createSecretStorageKey liegen.
    async restoreUnconfirmedRecoveryKey() {
        const encoded = this.crypto ? await this.loadUnconfirmedRecoveryKey() : null;
        if (!encoded) {
            return;
        }
        const defaultKey = await this.client.secretStorage.getKey();
        let key = null;
        try {
            key = defaultKey && decodeRecoveryKey(encoded);
        } catch (e) {
            key = null;
        }
        if (key && await this.client.secretStorage.checkKey(key, defaultKey[1])) {
            this.secretStorageKeys[defaultKey[0]] = key;
            this.publish({recoveryKey: encoded});
        } else if (defaultKey) {
            // Das Konto hat inzwischen einen anderen Recovery-Key.
            localStorage.removeItem(UNCONFIRMED_RECOVERY_KEY);
        }
    }

    // Ermittelt den Zustand neu.
    //
    // Während einer Aktion (Einrichtung, Reset, Recovery-Key) nicht: determinePhase
    // erzwingt ein /keys/query. Überholt das den Upload neuer Cross-Signing-Keys,
    // meldet der Server noch die alten, und das Rust-Crypto verwirft die neuen
    // privaten Keys als nicht passend. run() ermittelt den Zustand danach selbst.
    //
    // Vor dem ersten Sync ebenfalls nicht: Account-Daten wie der Secret-Storage-Key
    // fehlen dann noch, und der Zustand sähe unvollständiger aus, als er ist.
    async refresh() {
        if (!this.crypto || !this.synced || this.status.busy) {
            return;
        }
        try {
            await this.determinePhase();
        } catch (e) {
            console.error("Krypto-Zustand nicht ermittelbar: ", e);
        }
    }

    async determinePhase() {
        const userId = this.client.getUserId();
        const deviceId = this.client.getDeviceId();

        let phase;
        if (!await this.serverMasterKey()) {
            phase = "setup";
        } else {
            const deviceStatus = await this.crypto.getDeviceVerificationStatus(userId, deviceId);
            phase = deviceStatus?.crossSigningVerified ? "ready" : "unverified";
        }
        const backupActive = (await this.crypto.getActiveSessionBackupVersion()) !== null;
        this.publish({phase, backupActive});

        if (phase === "setup" && !this.setupAttempted && !this.status.busy) {
            this.setupAttempted = true;
            await this.run(() => this.setupNewIdentity());
        } else if (!this.completionAttempted && !this.status.busy && await this.setupIncomplete(phase)) {
            this.completionAttempted = true;
            await this.run(() => this.completeSetup(phase));
        }
    }

    // Nur die App, die die Identität angelegt hat, hält den privaten Master-Key; nur
    // sie kann eine unterbrochene Einrichtung vervollständigen. Geprüft wird gegen den
    // Server, nicht gegen den lokalen Backup-Status, der kurz nach dem Start noch
    // nicht aktuell ist.
    async setupIncomplete(phase) {
        if (!await this.hasPrivateCrossSigningKeys()) {
            return false;
        }
        if (phase === "unverified") {
            return true;
        }
        const defaultKeyId = await this.client.secretStorage.getDefaultKeyId();
        const backupInfo = await this.crypto.getKeyBackupInfo();
        const keyKnown = !!defaultKeyId && !!this.secretStorageKeys[defaultKeyId];
        const ready = await this.crypto.isSecretStorageReady();
        console.debug("Einrichtung vollständig? ", JSON.stringify({defaultKeyId, backup: !!backupInfo, keyKnown, ready}));
        if (!defaultKeyId || !backupInfo) {
            return true;
        }
        return keyKnown && !ready;
    }

    // Öffentlicher Master-Key des eigenen Kontos, wie ihn der Server kennt. Das
    // Rust-Crypto legt eine neue Identität lokal ab, bevor sie hochgeladen ist, und
    // beantwortet Fragen nach dem eigenen Konto aus diesem Stand; nach einem
    // gescheiterten Upload sähe es so aus, als gäbe es schon Keys.
    async serverMasterKey() {
        const userId = this.client.getUserId();
        const response = await this.client.http.authedRequest("POST", "/keys/query", undefined,
            {device_keys: {[userId]: []}});
        const keys = response.master_keys?.[userId]?.keys;
        return keys ? Object.values(keys)[0] : null;
    }

    // Private Cross-Signing-Keys, deren öffentliche Hälfte auf dem Server liegt
    async hasPrivateCrossSigningKeys() {
        const localMasterKey = await this.crypto.getCrossSigningKeyId();
        return localMasterKey !== null && localMasterKey === await this.serverMasterKey();
    }

    // Diese App hat die Cross-Signing-Keys selbst angelegt, die Einrichtung wurde aber
    // unterbrochen (z. B. App beim ersten Start geschlossen). Ohne Vervollständigung
    // bliebe das Konto ohne gültigen Recovery-Key oder ohne Key-Backup, obwohl die App
    // verifiziert aussieht.
    async completeSetup(phase) {
        const needsBackup = !await this.crypto.getKeyBackupInfo();
        const defaultKeyId = await this.client.secretStorage.getDefaultKeyId();

        if (!defaultKeyId) {
            await this.crypto.bootstrapSecretStorage({
                createSecretStorageKey: this.createSecretStorageKey,
                setupNewSecretStorage: true,
                setupNewKeyBackup: needsBackup,
            });
        } else if (this.secretStorageKeys[defaultKeyId]) {
            await this.crypto.bootstrapSecretStorage({setupNewKeyBackup: needsBackup});
        } else if (needsBackup) {
            // Ein bestehendes Secret Storage mit unbekanntem Key wird nie ersetzt: Sein
            // Recovery-Key wurde bestätigt oder stammt aus einer anderen App.
            console.warn("Kein Key-Backup, Secret-Storage-Key hier unbekannt; Backup nicht angelegt.");
        }
        if (phase === "unverified") {
            await this.crypto.crossSignDevice(this.client.getDeviceId());
        }
    }

    // Führt eine längere Aktion aus; Fehler landen im Status statt im Nirvana.
    async run(action) {
        this.publish({busy: true, error: ""});
        try {
            await action();
        } catch (e) {
            console.error("Krypto-Aktion fehlgeschlagen: ", e);
            this.publish({error: isNetworkError(e)
                ? "Keine Verbindung zum Server. Bitte später erneut versuchen – die Einrichtung wird auch beim nächsten Start der App fortgesetzt."
                : e.message || String(e)});
        } finally {
            this.publish({busy: false, approvalUrl: ""});
            await this.refresh();
        }
    }

    // Senden ist nur verifizierten Apps möglich (OnlySignedDevicesIsolationMode).
    reportSendFailure(e) {
        this.publish({
            error: this.status.phase === "ready"
                ? `Die Änderung wurde nicht gesendet: ${e.message}`
                : "Die Änderung wurde nicht gesendet, weil diese App noch nicht verifiziert ist. Sobald sie verifiziert ist, sendet Ziege sie automatisch.",
        });
    }

    async handleAction({action, key}) {
        switch (action) {
            case "refresh":
                return this.refresh();
            case "verify":
                return this.startOwnVerification();
            case "acceptVerification":
                return this.acceptRequest();
            case "cancelVerification":
                return this.cancelRequest();
            case "confirmEmojis":
                return this.confirmEmojis();
            case "rejectEmojis":
                return this.rejectEmojis();
            case "closeVerification":
                return this.closeVerification();
            case "setup":
                return this.run(() => this.setupNewIdentity());
            case "recoveryKey":
                return this.run(() => this.unlockWithRecoveryKey(key));
            case "recoveryKeySaved":
                localStorage.removeItem(UNCONFIRMED_RECOVERY_KEY);
                return this.publish({recoveryKey: "", recoveryKeyReplaced: false});
            case "resetIdentity":
                return this.run(() => this.resetIdentity());
            case "approvalDone":
                this.approvalWaiter?.resolve();
                return;
            case "approvalCancelled":
                this.approvalWaiter?.reject(new Error("Das Zurücksetzen wurde abgebrochen."));
                return;
            case "dismissError":
                return this.publish({error: ""});
            default:
                console.warn("Unbekannte Krypto-Aktion: ", action);
        }
    }


    // --- Einrichtung, Recovery-Key, Zurücksetzen ---

    // Erzeugt einen neuen Recovery-Key und hält ihn zur Anzeige im Status fest.
    // Ein noch unbestätigter Key aus einem abgebrochenen Versuch wird wiederverwendet,
    // damit der Nutzer nicht nacheinander zwei verschiedene zu sehen bekommt.
    createSecretStorageKey = async () => {
        const unconfirmed = await this.loadUnconfirmedRecoveryKey();
        const generated = unconfirmed
            ? {privateKey: decodeRecoveryKey(unconfirmed), encodedPrivateKey: unconfirmed}
            : await this.crypto.createRecoveryKeyFromPassphrase();
        localStorage.setItem(UNCONFIRMED_RECOVERY_KEY, JSON.stringify(await encryptJson(generated.encodedPrivateKey)));
        this.publish({recoveryKey: generated.encodedPrivateKey});
        return generated;
    };

    async loadUnconfirmedRecoveryKey() {
        const stored = localStorage.getItem(UNCONFIRMED_RECOVERY_KEY);
        return stored ? await decryptJson(JSON.parse(stored)) : null;
    }

    // Erstes Device eines Kontos: Cross-Signing, Secret Storage und Key-Backup anlegen.
    //
    // Der Server hat keine Cross-Signing-Keys (Phase „setup“), also werden immer neue
    // erzeugt. Lokal vorhandene stammen aus einem Versuch, dessen Upload nicht
    // ankam; bootstrapCrossSigning ohne setupNewCrossSigning hielte sie für erledigt
    // und lüde nichts hoch.
    async setupNewIdentity() {
        // Ein Secret Storage aus einem gescheiterten Versuch gehörte zu Keys, die nie
        // auf dem Server ankamen; sein Recovery-Key wird hier ersetzt.
        const replacesRecoveryKey = !!(await this.serverAccountData("m.secret_storage.default_key"))?.key
            && !await this.loadUnconfirmedRecoveryKey();
        this.publish({recoveryKeyReplaced: replacesRecoveryKey});
        // Sonst versuchte das SDK, die neuen Keys in dieses Secret Storage zu
        // exportieren, und scheiterte am unbekannten Key.
        if (replacesRecoveryKey) {
            await this.client.secretStorage.setDefaultKeyId(null);
        }
        await this.crypto.bootstrapCrossSigning({
            setupNewCrossSigning: true,
            authUploadDeviceSigningKeys: this.authUploadDeviceSigningKeys,
        });
        await this.crypto.bootstrapSecretStorage({
            createSecretStorageKey: this.createSecretStorageKey,
            setupNewSecretStorage: true,
            setupNewKeyBackup: true,
        });
    }

    // Verwirft Cross-Signing, Secret Storage und Key-Backup und legt alles neu an.
    // Ältere Nachrichten sind danach nur noch auf Devices lesbar, die ihre Keys haben.
    //
    // Die Freigabe in der Kontoverwaltung wird vorher eingeholt. Käme sie erst auf
    // Anforderung des Uploads, lägen die neuen privaten Keys während des Wartens
    // lokal vor, während der Server noch die alten öffentlichen meldet; das
    // Rust-Crypto verwirft sie dann als nicht passend, und hochgeladen würden Keys,
    // deren private Hälfte niemand mehr hat.
    async resetIdentity() {
        if (this.accountManagementUri) {
            const url = new URL(this.accountManagementUri);
            url.searchParams.set("action", "org.matrix.cross_signing_reset");
            await this.waitForApproval(url.toString());
        }
        await this.crypto.resetEncryption(this.authUploadDeviceSigningKeys);
        await this.crypto.bootstrapSecretStorage({
            createSecretStorageKey: this.createSecretStorageKey,
            setupNewSecretStorage: true,
            setupNewKeyBackup: true,
        });
    }

    async waitForApproval(approvalUrl) {
        try {
            await new Promise((resolve, reject) => {
                this.approvalWaiter = {resolve, reject};
                this.publish({approvalUrl});
            });
        } finally {
            this.approvalWaiter = null;
            this.publish({approvalUrl: ""});
        }
    }

    // Liegen bereits Cross-Signing-Keys auf dem Server, verlangt der Homeserver unter
    // MAS vor dem Ersetzen eine Freigabe in der Kontoverwaltung (UIA-Stage m.oauth,
    // bzw. org.matrix.cross_signing_reset nach MSC4312). Die holt resetIdentity vorher
    // ein; fehlt sie hier trotzdem, wird abgebrochen statt gewartet (siehe dort).
    authUploadDeviceSigningKeys = async (makeRequest) => {
        try {
            return await makeRequest(null);
        } catch (e) {
            if (this.approvalUrlFromError(e)) {
                throw new Error("Die Freigabe in der Kontoverwaltung fehlt oder ist abgelaufen (sie gilt 10 Minuten). Bitte das Zurücksetzen erneut starten.");
            }
            throw e;
        }
    };

    approvalUrlFromError(e) {
        if (e.httpStatus !== 401 || !e.data?.flows) {
            return null;
        }
        for (const stage of ["m.oauth", "org.matrix.cross_signing_reset"]) {
            const url = e.data.params?.[stage]?.url;
            if (url) {
                return url;
            }
        }
        return null;
    }

    async unlockWithRecoveryKey(input) {
        let key;
        try {
            key = decodeRecoveryKey((input || "").trim());
        } catch (e) {
            throw new Error("Das ist kein gültiger Recovery-Key. Bitte genau so eingeben, wie er notiert wurde; Groß- und Kleinschreibung zählt, Leerzeichen nicht.");
        }

        const secretStorage = this.client.secretStorage;
        const defaultKey = await secretStorage.getKey();
        if (!defaultKey) {
            throw new Error("Für dieses Konto ist kein Recovery-Key eingerichtet.");
        }
        const [keyId, keyInfo] = defaultKey;
        if (!await secretStorage.checkKey(key, keyInfo)) {
            throw new Error("Dieser Recovery-Key gehört nicht zu diesem Konto.");
        }

        // Ohne Cross-Signing-Keys im Secret Storage würde bootstrapCrossSigning neue
        // Keys anlegen, also die Identität zurücksetzen. Das darf hier nicht passieren.
        if (!await secretStorage.isStored("m.cross_signing.master")) {
            throw new Error("Im Schlüsselspeicher liegen keine Signaturschlüssel. Bitte mit einer anderen App verifizieren.");
        }

        this.secretStorageKeys[keyId] = key;
        await this.crypto.bootstrapCrossSigning({});

        try {
            await this.crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            await this.crypto.checkKeyBackupAndEnable();
        } catch (e) {
            console.warn("Key-Backup nicht aus dem Secret Storage ladbar: ", e);
        }
        this.restoreBackupInBackground();
    }

    restoreBackupInBackground() {
        if (this.restoring) {
            return;
        }
        this.restoring = this.crypto.restoreKeyBackup()
            .then((result) => console.log("Key-Backup wiederhergestellt: ", result))
            .catch((e) => console.warn("Key-Backup nicht wiederhergestellt: ", e))
            .finally(() => { this.restoring = null; });
    }


    // --- Verifikation per SAS ---

    publishVerification(changes) {
        this.publish({verification: changes === null ? null : {
            phase: "waiting",
            otherApp: this.otherAppName,
            emojis: [],
            message: "",
            ownRequest: false,
            ...this.status.verification,
            ...changes,
        }});
    }

    replaceRequest(request) {
        if (this.request && this.request !== request && this.request.pending) {
            this.request.cancel().catch(() => {});
        }
        this.request = request;
        this.sas = null;
        this.otherAppName = "";
        this.lookUpOtherApp(request);
        request.on(VerificationRequestEvent.Change, () => this.onRequestChange(request));
    }

    // Name der Gegenseite, wie ihn die Sitzungsliste des Kontos führt, z. B. „Element“
    async lookUpOtherApp(request) {
        const deviceId = request.otherDeviceId;
        if (!deviceId) {
            return;
        }
        try {
            const devices = await this.crypto.getUserDeviceInfo([request.otherUserId]);
            const name = devices.get(request.otherUserId)?.get(deviceId)?.displayName;
            this.otherAppName = name ? `${name} (${deviceId})` : deviceId;
        } catch (e) {
            this.otherAppName = deviceId;
        }
        if (request === this.request && this.status.verification) {
            this.publishVerification({otherApp: this.otherAppName});
        }
    }

    async startOwnVerification() {
        try {
            this.publish({verification: null});
            // Solange eine frühere Anfrage läuft, bricht der Schlüsselspeicher eine neue
            // an dasselbe Konto samt der früheren ab; die frühere endet deshalb zuerst.
            const previous = this.request;
            this.request = null;
            if (previous?.pending) {
                await previous.cancel().catch(() => {});
            }
            this.replaceRequest(await this.crypto.requestOwnUserVerification());
            this.publishVerification({phase: "waiting", ownRequest: true});
        } catch (e) {
            console.error("Verifikationsanfrage fehlgeschlagen: ", e);
            this.publish({error: e.message});
        }
    }

    onIncomingRequest(request) {
        if (!request.isSelfVerification) {
            console.info("Verifikationsanfrage eines anderen Nutzers ignoriert: ", request.otherUserId);
            return;
        }
        // War die App geschlossen, kommen Anfrage und Abbruch zusammen an; die Anfrage
        // ist dann schon beendet und ändert sich nicht mehr.
        if (!request.pending) {
            console.info("Bereits beendete Verifikationsanfrage ignoriert: ", request.otherDeviceId, request.phase);
            return;
        }
        this.publish({verification: null});
        this.replaceRequest(request);
        this.publishVerification({phase: "incoming"});
    }

    async acceptRequest() {
        const request = this.request;
        if (request && !request.pending) {
            this.publishVerification({phase: "cancelled", emojis: [], message: STALE_REQUEST_MESSAGE});
            return;
        }
        try {
            await request?.accept();
            this.publishVerification({phase: "waiting"});
        } catch (e) {
            console.error("Annehmen der Verifikation fehlgeschlagen: ", e);
            if (request && !request.pending) {
                this.publishVerification({phase: "cancelled", emojis: [], message: STALE_REQUEST_MESSAGE});
            } else {
                this.publish({error: e.message});
            }
        }
    }

    async cancelRequest() {
        const request = this.request;
        this.request = null;
        this.sas = null;
        this.publish({verification: null});
        if (request?.pending) {
            await request.cancel().catch(() => {});
        }
    }

    async confirmEmojis() {
        const sas = this.sas;
        if (!sas) {
            return;
        }
        this.publishVerification({phase: "confirmed"});
        try {
            await sas.confirm();
        } catch (e) {
            console.error("Bestätigung fehlgeschlagen: ", e);
        }
    }

    rejectEmojis() {
        this.sas?.mismatch();
    }

    closeVerification() {
        this.request = null;
        this.sas = null;
        this.publish({verification: null});
    }

    onRequestChange(request) {
        if (request !== this.request) {
            return;
        }

        switch (request.phase) {
            case VerificationPhase.Ready:
                // Bei eigenen Anfragen steht erst jetzt fest, welche App geantwortet hat.
                if (!this.otherAppName) {
                    this.lookUpOtherApp(request);
                }
                if (request.initiatedByMe) {
                    this.startSas(request);
                } else {
                    setTimeout(() => {
                        if (request === this.request && request.phase === VerificationPhase.Ready && !request.verifier) {
                            this.startSas(request);
                        }
                    }, SAS_START_FALLBACK_MS);
                }
                break;

            case VerificationPhase.Started:
                if (request.verifier && !this.sas) {
                    this.followVerifier(request.verifier);
                }
                break;

            case VerificationPhase.Done:
                this.sas = null;
                this.publishVerification({phase: "done", emojis: []});
                this.afterOwnVerification();
                break;

            case VerificationPhase.Cancelled:
                this.sas = null;
                this.publishVerification({
                    phase: "cancelled",
                    emojis: [],
                    message: CANCELLATION_MESSAGES[request.cancellationCode]
                        || `Die Verifikation wurde abgebrochen (${request.cancellationCode}).`,
                });
                break;
        }
    }

    async startSas(request) {
        try {
            if (!request.verifier) {
                await request.startVerification(SAS_METHOD);
            }
        } catch (e) {
            console.error("SAS-Start fehlgeschlagen: ", e);
        }
    }

    followVerifier(verifier) {
        if (this.followedVerifiers.has(verifier)) {
            return;
        }
        this.followedVerifiers.add(verifier);

        verifier.on(VerifierEvent.ShowSas, (sas) => {
            this.sas = sas;
            this.publishVerification({
                phase: "emojis",
                emojis: (sas.sas.emoji || []).map(([emoji, name]) => ({
                    emoji,
                    name: SAS_EMOJI_NAMES_DE[name.toLowerCase()] || name,
                })),
            });
        });
        verifier.verify().catch((e) => console.info("Verifikation beendet: ", e?.message || e));
    }

    async afterOwnVerification() {
        try {
            await this.crypto.checkKeyBackupAndEnable();
        } catch (e) {
            console.warn("Key-Backup nach Verifikation nicht aktivierbar: ", e);
        }
        await this.refresh();
    }
}


function isNetworkError(e) {
    return e?.name === "ConnectionError" || e instanceof TypeError && /fetch/i.test(e.message)
        || /fetch failed|Failed to fetch|NetworkError/i.test(e?.message ?? "");
}
